import { ChevronLeftIcon, PlusIcon, ZapIcon } from "lucide-react";
import Link from "next/link";
import type { ComponentProps, MouseEventHandler, ReactNode } from "react";

import { Button } from "@/components/ui/button";
import type { ChannelTab } from "./channel-view-options";

/*
 * The channel's header actions and section links as markup. The channel has
 * no section tabs: its Overview links to each section's full page, and the
 * title row carries Add and New session. ChannelWorkspaceToolbar and
 * ChannelHome render these, so a fixture channel composes the same header.
 */

/** A section's full page: Data, Feed, Memory or Files. */
export type ChannelSection = Extract<ChannelTab, "data" | "feed" | "memory" | "files">;

const SECTION_LABELS: Record<ChannelSection, string> = {
  data: "Data sets",
  feed: "Activity",
  memory: "Memory",
  files: "Files",
};

/** A section's name, as its full page's title and its Overview heading read it. */
export function channelSectionLabel(section: ChannelSection): string {
  return SECTION_LABELS[section];
}

/** The section a view belongs to, or null on the Overview and inside a data set. */
export function channelSectionOf(view: ChannelTab): ChannelSection | null {
  return view === "data" || view === "feed" || view === "memory" || view === "files"
    ? view
    : null;
}

/** Opens the channel's memory: a small icon beside its access, marked while open. */
export function ChannelMemoryButton({
  href,
  current = false,
  onClick,
}: {
  href: string;
  /** The memory page is open. */
  current?: boolean;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
}) {
  return (
    <Button
      size="icon-sm"
      variant="outline"
      nativeButton={false}
      aria-label="Channel memory"
      title="Channel memory"
      aria-current={current ? "page" : undefined}
      className={current ? "bg-muted text-foreground" : undefined}
      render={<Link href={href} onClick={onClick} />}
    >
      <ZapIcon className="size-3.5" aria-hidden />
    </Button>
  );
}

/** The channel's closed "Add" menu button; the menu's trigger props pass through. */
export function ChannelAddMenuTrigger(
  props: Omit<ComponentProps<typeof Button>, "children" | "size" | "variant">,
) {
  return (
    <Button size="sm" variant="ghost" aria-label="Add to channel" {...props}>
      <PlusIcon /> Add
    </Button>
  );
}

/** An Overview section's heading, with "See all" opening the section's full page. */
export function ChannelSectionHeadingView({
  title,
  href,
  onClick,
  children,
}: {
  title: string;
  /** The section's full page; without one the heading has no link. */
  href?: string;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  /** More controls beside the link, such as a count. */
  children?: ReactNode;
}) {
  return (
    <div className="flex min-h-6 items-center justify-between gap-3">
      <h2 className="text-xs font-medium text-muted-foreground">{title}</h2>
      <div className="flex shrink-0 items-center gap-3">
        {children}
        {href ? (
          <Link
            href={href}
            onClick={onClick}
            className="rounded-sm text-xs text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            See all
          </Link>
        ) : null}
      </div>
    </div>
  );
}

/**
 * A session pane's channel row: back to the Overview from a section, and the
 * channel's actions. The pane names the channel, so the row has no title.
 */
export function ChannelPaneHeaderView({
  back,
  section,
  actions,
}: {
  /** The Overview, while a section or data set is open. */
  back?: { href: string; onClick?: MouseEventHandler<HTMLAnchorElement> } | null;
  /** The open section or data set's name. */
  section?: string | null;
  actions?: ReactNode;
}) {
  return (
    <div className="flex h-10 w-full shrink-0 items-center gap-1.5 border-b border-border/60 pl-2 pr-2 text-sm">
      {back ? (
        <Link
          href={back.href}
          onClick={back.onClick}
          className="inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ChevronLeftIcon className="size-3.5" aria-hidden />
          Overview
        </Link>
      ) : null}
      {section ? (
        <span aria-current="page" className="min-w-0 truncate text-xs font-medium">
          {section}
        </span>
      ) : null}
      <div className="ml-auto flex shrink-0 items-center gap-1">{actions}</div>
    </div>
  );
}
